import type { Moment } from 'moment';

import { GENDER } from '../constants/accounts';
import { PREVIOUS_PRO_CHOICES, PRO_CHOICE_SOURCES } from '../constants/pro-accounts';
import type { Pro, ProChoiceValue, ProGender } from '../types/commons';
import type { CommonProChoice, ProChoice, ProChoiceAPI } from '../types/customer';
import moment from './moment';

export const isNotWanted = ({ value }: ProChoice): boolean =>
  value === PREVIOUS_PRO_CHOICES.EXCEPT || value === PREVIOUS_PRO_CHOICES.NOT_THIS_TIME;

export const isWanted = ({ value }: CommonProChoice): boolean =>
  value === PREVIOUS_PRO_CHOICES.ONLY || value === PREVIOUS_PRO_CHOICES.IF_POSSIBLE;

export const areExclusive = (choices: CommonProChoice[]): boolean => {
  const wantedPros = choices.filter(isWanted);
  return !!wantedPros.length && wantedPros.every(({ value }) => value === PREVIOUS_PRO_CHOICES.ONLY);
};

// TODO: We need to remove this function as soon as we remove our pro choices object structure
export const transformBackChoices = (choices?: ProChoiceAPI[]): CommonProChoice[] => {
  if (!choices?.length) return [];

  return choices.map((choice) => ({ ...choice, value: choice.choice }));
};

export const hasMultiplePreferredPros = (choices: CommonProChoice[]): boolean =>
  choices.filter(({ value }) => value === PREVIOUS_PRO_CHOICES.IF_POSSIBLE).length > 1;

export const getWantedChoicesProNames = (choices: CommonProChoice[]): string[] =>
  choices.filter(isWanted).map(({ pro }) => pro.first_name);

export const getWantedChoicesProsGender = (choices?: CommonProChoice[]): Exclude<ProGender, 'dont_care'> => {
  if (!choices?.length) return 'man';
  const onlyFemales = choices.filter(isWanted).every(({ pro }) => pro.gender === GENDER.WOMAN);
  return onlyFemales ? 'woman' : 'man';
};

export const hasPreferred = (choices: CommonProChoice[]): boolean =>
  choices.some(({ value }) => value === PREVIOUS_PRO_CHOICES.IF_POSSIBLE);

export const hasProProfileChoice = (choices: ProChoice[]): boolean =>
  choices.some((choice) => choice.source === PRO_CHOICE_SOURCES.PRO_PROFILE);

export const hasInvalidProChoices = (choices: Partial<ProChoice>[]): choices is ProChoice[] =>
  choices.some((choice) => !choice?.source || !choice.pro);

export const isProFromProProfile = (choices: ProChoice[]): boolean =>
  choices.length === 1 && hasProProfileChoice(choices);

export const isAbsent = (pro: Pro, date: Moment): boolean =>
  pro.absences.some(({ off_from, off_until }) =>
    date.isBetween(moment(off_from).startOf('day'), moment(off_until).endOf('day'), undefined, '[]'),
  );

export const switchChoices = (choices: ProChoice[], toRemove: ProChoiceValue, toAdd: ProChoiceValue): ProChoice[] =>
  choices.map((choice) => (choice.value === toRemove ? { ...choice, value: toAdd } : choice));
